import React, { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Select } from '@firesoon/antd';
import { SelectProps, SelectValue } from '@firesoon/antd/lib/select';
import classnames from 'classnames';
import DropdownRender from './DropdownRender';
import { DROPDOWN_HEIGHT, SELECT_MODE, ITEM_HEIGHT, ITEM_ELEMENT_NUMBER } from '../constants';
import { useContext } from '../context';
import { isEmpty } from 'lodash';
import { useMemoizedFn } from 'ahooks';

const VirtualSelect: FC<SelectProps> = (props) => {
  const {
    value: propsValue,
    showSearch,
    mode,
    dropdownClassName,
    dropdownStyle,
    filterOption = true,
    optionFilterProp = 'value',
    children,
    notFoundContent,
    onChange,
    onSearch,
    onDropdownVisibleChange,
    onInputKeyDown,
  } = props;
  const eventTimerRef = useRef<any>(null);
  const scrollEleRef = useRef<Element | null>(null);
  const prevScrollTop = useRef(0); // 上一次滚动的scrollTop值
  const scrollTopRef = useRef(0);
  const dropDownRef = useRef<any>(null);
  const [middleValue, setMiddleValue] = useState<SelectValue | undefined>(undefined);
  const [filterChildren, setFilterChildren] = useState<ReactElement[] | null>(null); // 过滤的列表
  // 判断外部是否传入虚拟滚动值
  const { virtual } = useContext();
  const _virtual = typeof virtual === 'object' ? virtual : { itemSize: ITEM_HEIGHT };
  const { itemSize, height = DROPDOWN_HEIGHT } = _virtual || {};

  const isMultiple = SELECT_MODE.includes(mode); // 是否为多选
  const childrenList = React.Children.toArray(children) as ReactElement[]; // children列表
  const allHeight = (filterChildren || childrenList).length * itemSize || 100; // 列表总高度

  const myDropdownClassName = useMemo(() => `fs-${Number(new Date())}`, []);
  const id = useMemo(() => `sid${Number(new Date())}`, []);

  const value = propsValue || middleValue; // 最终映射的value

  /** 设置滚动 */
  const onScroll = () => {
    const { scrollTop = 0 } = scrollEleRef.current || {};
    scrollTopRef.current = scrollTop as number;
    // 滚动高度
    let delta = Math.abs(prevScrollTop.current - scrollTop);
    // 滚动时重新渲染的scrollTop判断值，大于 reactDelta 则刷新下拉列表
    if (delta > (itemSize * ITEM_ELEMENT_NUMBER) / 3) {
      prevScrollTop.current = scrollTop;
      dropDownRef?.current?.setStartAndEndIndex(scrollTop);
    }
  };

  /** 设置滚动事件 */
  const addScrollEvent = () => {
    const scrollEle = document.querySelector(`.${myDropdownClassName}`);
    scrollEleRef.current = scrollEle;
    if (!scrollEle) {
      return;
    }
    scrollEle.addEventListener('scroll', onScroll, false);
  };

  const setSuperDropDownMenu = () => {
    /** 用来判断第一次给元素增加scroll方法 */
    if (!eventTimerRef.current) {
      eventTimerRef.current = setTimeout(() => {
        addScrollEvent();
      }, 0);
    } else {
      dropDownRef?.current?.setStartAndEndIndex(scrollTopRef.current);
    }
  };
  /** 如果已有value，设置默认滚动位置 */
  const setDefaultScrollTop = () => {
    const scrollEle = scrollEleRef.current;
    if (!scrollEle) return;
    // 使用for可以中断循环
    for (let index = 0; index < childrenList.length; index++) {
      const item = childrenList[index];
      const itemValue = item.props.value;
      // @ts-ignore
      if (itemValue === value || (Array.isArray(value) && value.includes(itemValue))) {
        const targetScrollTop = index * itemSize;
        const targetScrollUp = targetScrollTop - height + itemSize;
        setTimeout(() => {
          // 当value滚动到面板上部分
          if (scrollEle.scrollTop > targetScrollTop && scrollEle) {
            scrollEle.scrollTop = targetScrollTop;
          }
          // 当value滚动到面板下部分
          if (scrollEle.scrollTop < targetScrollUp && scrollEle) {
            scrollEle.scrollTop = targetScrollUp;
          }
        }, 100);
        return;
      }
    }
  };
  /** 展开下拉菜单的回调 判断列表显示数据 */
  const handleDropdownVisibleChange = useMemoizedFn((visible: boolean) => {
    onDropdownVisibleChange?.(visible);
    if (!visible) {
      const scrollEle = scrollEleRef.current;
      if (isEmpty(value) && scrollEle) {
        scrollEle.scrollTop = 0;
        scrollTopRef.current = 0;
        dropDownRef?.current?.setStartAndEndIndex(0);
      }
      // 定时器确保关闭后再设置 filterChildren,防止列表刷新闪烁
      setTimeout(() => {
        setFilterChildren(null);
      });
      return;
    }
    setSuperDropDownMenu();
    setDefaultScrollTop();
  });

  const handleChange = (value: SelectValue, opt: React.ReactElement<any> | React.ReactElement<any>[]) => {
    setMiddleValue(value);
    onChange?.(value, opt);
  };

  /** 搜索重新计算下拉滚动高度 */
  const handleSearch = (v: string) => {
    const scrollEle = scrollEleRef.current;
    if ((showSearch || isMultiple) && filterOption !== false) {
      const _filterChildren = (() => {
        const typeFnFilterOption = typeof filterOption === 'function';
        // 自定义过滤对应的 option 属性配置
        const filterChildren: any = (() => {
          if (typeFnFilterOption) {
            return childrenList.filter((item) => filterOption(v, item));
          } else {
            return childrenList.filter((item) => `${item.props[optionFilterProp]}`.indexOf(v) >= 0);
          }
        })();

        // const allChildrenValues = childrenList.map((item) => {
        //   return item.props?.[typeFnFilterOption ? 'children' : optionFilterProp];
        // });
        // tag模式 输入搜索不存在会自动添加一个筛选项 需要进行判断
        if (mode === 'tags') {
          filterChildren.push(v);
        }
        return filterChildren;
      })();
      setFilterChildren(_filterChildren);
      setTimeout(() => {
        // TODO 搜索后重新滚动到顶部， 防止上次scrollTop无效
        if (_filterChildren && scrollEle) {
          scrollTopRef.current = 0;
          scrollEle.scrollTop = 0;
        }
        // 搜索成功后重新计算列表高度
        setSuperDropDownMenu();
      }, 0);
    }
    onSearch?.(v);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isMultiple) {
      setFilterChildren(null);
    }
    onInputKeyDown?.(e);
  };

  useEffect(() => {
    const scrollEle = scrollEleRef.current;
    // open或者defaultOpen默认为true时添加滚动事件
    setTimeout(() => {
      addScrollEvent();
    }, 500);
    return () => {
      if (!scrollEle) return;
      eventTimerRef.current = scrollEle.removeEventListener('scroll', onScroll, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Select
      {...props}
      value={value}
      id={id}
      onSearch={handleSearch}
      onChange={handleChange}
      onInputKeyDown={handleInputKeyDown}
      dropdownClassName={classnames(myDropdownClassName, dropdownClassName)}
      dropdownStyle={{
        maxHeight: `${height}px`,
        ...dropdownStyle,
        overflow: 'auto',
        position: 'relative',
      }}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      // eslint-disable-next-line react/no-unstable-nested-components
      dropdownRender={(menu: any) => {
        if (childrenList.length === 0) {
          return <div style={{ padding: '5px 12px' }}>{notFoundContent}</div>;
        }
        return <DropdownRender ref={dropDownRef} allHeight={allHeight} menu={menu} />;
      }}
    />
  );
};

export default VirtualSelect;
